import { cn } from 'lib/utils';
import { FC, useMemo, useRef } from 'react';
import { uiSimpleBox } from '../CutCorners';
import ToastIcon from './ToastIcon';
import BPIcon from '../icons/BPIcon';
import TransactionLink, { Transaction } from './TransactionLink';
import useNotifyStore from 'stores/notifyStore';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';

export type ToastVariant =
  | 'success'
  | 'error'
  | 'spinner_blue'
  | 'spinner_orange';

interface BPToastProps {
  variant: ToastVariant;
  message: string;
  description?: string;
  transaction?: Transaction | null;
}

const BPToast: FC<BPToastProps> = ({
  variant,
  message,
  description = null,
  transaction = null,
}) => {
  const toastRef = useRef<HTMLDivElement>(null);
  const { setCurrentToast } = useNotifyStore();

  useGSAP(() => {
    gsap.set(toastRef.current, { x: 550, alpha: 0 });
    gsap.to(toastRef.current, {
      x: 0,
      alpha: 1,
      duration: 0.4,
      ease: 'power3.out',
    });

    const timeout = setTimeout(onClose, 5000);
    return () => clearTimeout(timeout);
  }, []);

  const subTitle = useMemo<string | JSX.Element | null>(() => {
    if (!transaction && !description) return null;
    if (transaction) return <TransactionLink transaction={transaction} />;
    return description ?? null;
  }, [transaction, description]);

  const onClose = () => {
    gsap.to(toastRef.current, {
      alpha: 0,
      scale: 0.9,
      duration: 0.2,
      ease: 'expo.in',
      onComplete: () => setCurrentToast(null),
    });
  };

  return (
    <div
      ref={toastRef}
      className={cn(
        'grid relative grid-cols-[auto_1fr] gap-5 items-center justify-start text-left w-[380px] h-[88px] py-[20px] px-[24px] bg-[#1F2124]/60 backdrop-blur-lg pointer-events-auto',
        uiSimpleBox.bg
      )}
    >
      <Background />
      <div className="grid relative items-center">
        <ToastIcon variant={variant} />
      </div>
      <div className="grid relative w-full grid-flow-row gap-[2px]">
        <div className="text-[16px]">{message}</div>
        {subTitle && (
          <div className="text-[14px] text-bionic-muted">{subTitle}</div>
        )}
      </div>
      <button
        onClick={onClose}
        className="block absolute top-[10px] right-[10px]"
      >
        <BPIcon
          icon="close"
          className="fill-[#fff] opacity-50 scale-90 hover:opacity-100"
        />
      </button>
    </div>
  );
};

const Background = () => (
  <div
    className={cn(
      'block absolute top-0 left-0 w-full h-full bg-gradient-to-b from-white/30 to-white/10',
      uiSimpleBox.border
    )}
  />
);

export default BPToast;
